import React, { useState } from "react";
import { useOrder } from "../context/OrderContext";
import { MAX_CART_ITEMS } from "../config/constants";
import { formatPrice } from "../utils/currency";
import "./RecipePopup.css";

const RecipePopup = ({ recipe, onClose, onCheckout, currency }) => {
  const { cart, addToCartWithQuantity, updateQuantity } = useOrder();
  const [quantity, setQuantity] = useState(1);

  const existingItem = cart.find((item) => item.recipe_id === recipe.recipe_id);
  const currentQuantity = existingItem ? existingItem.quantity : 0;
  const totalItemsInCart = cart.reduce((sum, item) => sum + item.quantity, 0);
  const remainingSlots = MAX_CART_ITEMS - totalItemsInCart;
  const maxAllowed = existingItem ? remainingSlots + currentQuantity : remainingSlots;

  const handleIncrease = () => {
    if (quantity < maxAllowed) {
      setQuantity(quantity + 1);
    }
  };

  const handleDecrease = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const applyQuantityToCart = () => {
    const totalAfterAdd = totalItemsInCart + quantity;
    if (totalAfterAdd > MAX_CART_ITEMS) return false;
    if (existingItem) {
      const newQuantity = currentQuantity + quantity;
      updateQuantity(recipe.recipe_id, newQuantity);
    } else {
      addToCartWithQuantity(recipe, quantity);
    }
    return true;
  };

  const handleAddToCart = () => {
    if (applyQuantityToCart()) onClose();
  };

  const handleCheckout = () => {
    if (applyQuantityToCart()) {
      requestAnimationFrame(() => onCheckout());
    }
  };

  const isMaxReached = totalItemsInCart >= MAX_CART_ITEMS;
  const itemTotal = recipe.recipe_price * quantity;

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-card" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="popup-close" onClick={onClose}>×</button>

        {/* Image Section */}
        <div className="popup-image-section">
          <img
            src={recipe.recipe_image || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect fill='%23f0f0f0' width='300' height='200'/%3E%3C/svg%3E"}
            alt={recipe.recipe_name}
            onError={(e) => {
              e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect fill='%23f0f0f0' width='300' height='200'/%3E%3C/svg%3E";
            }}
          />
          <div className="popup-image-overlay">
            <span className="popup-category">{recipe.recipe_category}</span>
            <span className="popup-cook-time">⏱ {recipe.preparation_time || 0}m</span>
          </div>
        </div>

        {/* Content Section */}
        <div className="popup-content">
          <div className="popup-header">
            <h2>{recipe.recipe_name}</h2>
            <div className="popup-meta">
              <span className="popup-price">{formatPrice(recipe.recipe_price, currency)}</span>
            </div>
          </div>

          {/* Quantity Section */}
          <div className="popup-quantity">
            <button
              className="qty-btn"
              onClick={handleDecrease}
              disabled={quantity <= 1}
            >
              −
            </button>
            <div className="qty-value">
              <span className="qty-number">{quantity}</span>
              <span className="qty-total">{formatPrice(itemTotal, currency)}</span>
            </div>
            <button
              className="qty-btn"
              onClick={handleIncrease}
              disabled={quantity >= maxAllowed || isMaxReached}
            >
              +
            </button>
          </div>

          {/* Status Info */}
          {existingItem && (
            <p className="popup-info">Already in cart: {currentQuantity}</p>
          )}
          {isMaxReached && (
            <p className="popup-warning">Maximum {MAX_CART_ITEMS} items reached</p>
          )}
          {!isMaxReached && remainingSlots > 0 && remainingSlots < 3 && (
            <p className="popup-slots">{remainingSlots} slot{remainingSlots !== 1 ? "s" : ""} left</p>
          )}

          {/* Action Buttons */}
          <div className="popup-actions">
            <button
              className="popup-btn btn-secondary"
              onClick={handleAddToCart}
              disabled={isMaxReached}
            >
              {isMaxReached ? "Cart Full" : "Add More"}
            </button>
            <button
              className="popup-btn btn-primary"
              onClick={handleCheckout}
            >
              Checkout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipePopup;
